import { useState } from "react";
import { Link } from "react-router-dom";
import { CircleHelp, Play, Square } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { CatalogSituation } from "@/lib/catalog";
import { LEVEL_LABELS } from "@/lib/catalog";
import { sceneBrief } from "@/lib/sceneCast";
import { sceneMinutes } from "@/lib/missions";
import { useTextToSpeech } from "@/hooks/useTextToSpeech";

type NextSceneCardProps = {
  situation: CatalogSituation;
  ctaLabel: string;
  summaryLine?: string | null;
  to?: string;
};

export const NextSceneCard = ({ situation, ctaLabel, summaryLine, to }: NextSceneCardProps) => {
  const brief = sceneBrief(situation);
  const { speak, isPlaying, error } = useTextToSpeech();
  const [showHelp, setShowHelp] = useState(false);
  const stageHref = to ?? `/app/practice/${situation.slug}`;

  return (
    <article className="rounded-lg bg-blue-500 shadow-brand">
      <div className="flex items-center gap-4 px-6 pt-6">
        <img
          src={brief.partner.src}
          alt={brief.partner.name}
          className="h-14 w-14 rounded-full object-cover object-[center_18%] ring-2 ring-white/30"
        />
        <div className="min-w-0">
          <h2 className="truncate text-h3 text-white">
            {brief.sceneTitle}
          </h2>
          <p className="mt-0.5 text-body-sm text-blue-100">
            {brief.partner.name} · {LEVEL_LABELS[situation.level].toLowerCase()} · ~{sceneMinutes(situation)} min
          </p>
        </div>
      </div>

      {summaryLine && (
        <p className="mt-3 px-6 text-body-sm leading-relaxed text-blue-100">{summaryLine}</p>
      )}

      <div className="mt-5 border-t border-white/15 px-6 pb-6 pt-5">
        <p className="text-[19px] font-bold leading-snug text-white">
          &ldquo;{brief.partnerOpen}&rdquo;
        </p>

        <div className="mt-5 flex items-center justify-center gap-5">
          <button
            type="button"
            onClick={() => void speak(brief.partnerOpen)}
            aria-label={isPlaying ? "Parar a fala" : "Ouvir a fala"}
            className="flex h-12 w-12 items-center justify-center rounded-full bg-white/20 text-white transition hover:bg-white/30"
          >
            {isPlaying ? (
              <Square className="h-4 w-4 fill-current" />
            ) : (
              <Play className="h-5 w-5 fill-current" />
            )}
          </button>
          <button
            type="button"
            onClick={() => setShowHelp((open) => !open)}
            aria-label="Ajuda em português"
            className="flex h-12 w-12 items-center justify-center rounded-full bg-white/20 text-white transition hover:bg-white/30"
          >
            <CircleHelp className="h-5 w-5" />
          </button>
        </div>

        {error ? (
          <p className="mt-4 text-center text-body-sm text-orange-300">{error}</p>
        ) : null}

        {showHelp && (
          <p className="mt-4 text-center text-body-sm leading-relaxed text-blue-100">
            {brief.guideOpen}
          </p>
        )}

        <Button asChild variant="primary-inverse" className="mt-5 w-full">
          <Link to={stageHref}>{ctaLabel}</Link>
        </Button>
      </div>
    </article>
  );
};
